import type { LatLng } from "@oshimap/domain";

/**
 * 길찾기.
 *
 * RouteProvider SDK 는 아직 연결하지 않았습니다 (27 §Route — 한국은 NAVER
 * 우선 후보이고 아직 결정 전입니다). 이 함수는 **외부 앱 열기**이며 경로
 * 계산이 아닙니다.
 *
 * ## 왜 `geo:` 를 모든 기기에서 쓰지 않는가
 *
 * `geo:` 는 **Android 규약**입니다. iOS Safari 는 그 스킴을 처리하지 않습니다.
 * 그런데 전에는 터치가 되면(=아이폰 포함) `geo:` 를 걸고 곧바로 `"opened"` 를
 * 돌려줬습니다. 브라우저는 모르는 스킴에 조용히 아무 일도 하지 않으므로,
 * **아이폰에서 길찾기 버튼은 눌러도 아무 일이 없었고 화면은 아무 말도 하지
 * 않았습니다.** 스킴 할당은 실패해도 예외를 던지지 않아 `catch` 도 소용이
 * 없습니다.
 *
 * 어느 지도 앱을 열 것인가(Apple·Google·NAVER·카카오)는 제품 결정이라 여기서
 * 고르지 않습니다. 대신 **모르면 모른다고 하고** 붙여넣을 수 있게 합니다.
 */
export type DirectionsResult = "opened" | "copied" | "failed";

/** 앱이 열렸는지 확인할 때까지 기다리는 시간. 열리면 이 창은 그 전에 숨습니다. */
const HANDOFF_MS = 1200;

function isAndroid(): boolean {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

/**
 * 스킴이 처리됐는지 봅니다. 처리됐으면 브라우저가 앱으로 넘어가며 이 문서가
 * 숨습니다 — 그것 말고는 알 방법이 없습니다. 정확한 신호는 아니지만,
 * **아무 일도 안 하고 "열었다" 고 말하는 것보다는 참에 가깝습니다.**
 */
async function handedOff(): Promise<boolean> {
  if (typeof document === "undefined") return false;
  return new Promise((resolve) => {
    let done = false;
    const finish = (value: boolean) => {
      if (done) return;
      done = true;
      document.removeEventListener("visibilitychange", onHide);
      resolve(value);
    };
    const onHide = () => { if (document.visibilityState === "hidden") finish(true); };
    document.addEventListener("visibilitychange", onHide);
    setTimeout(() => finish(document.visibilityState === "hidden"), HANDOFF_MS);
  });
}

export async function openDirections(
  point: LatLng,
  name: string,
  address: string,
): Promise<DirectionsResult> {
  if (isAndroid()) {
    const label = encodeURIComponent(name);
    const uri = `geo:${point.latitude},${point.longitude}?q=${point.latitude},${point.longitude}(${label})`;
    try {
      window.location.href = uri;
      if (await handedOff()) return "opened";
    } catch { /* 아래 복사로 갑니다 */ }
  }

  /* 아무 일도 안 일어나는 것보다는 붙여넣을 수 있는 편이 낫습니다. */
  try {
    await navigator.clipboard.writeText(`${name} — ${address}`);
    return "copied";
  } catch {
    return "failed";
  }
}
